import { BEHAVIOR_ASSESSMENT_VERSION, BEHAVIOR_QUESTIONS } from './behavior-assessment';

export const LEGACY_DRAFT_KEY = `untangled-pattern-assessment:${BEHAVIOR_ASSESSMENT_VERSION}`;
export const DRAFT_TTL = 7 * 24 * 3600_000;
const CONTEXT = `${LEGACY_DRAFT_KEY}:context-v2`;
const HANDOFF = `${LEGACY_DRAFT_KEY}:handoff-v2`;
export type AssessmentDraft = {
  version: string; answers: Record<string, number>; pageIndex: number; activeSeconds: number;
  started: boolean; adultConfirmed: boolean; profilingConsent: boolean; researchConsent: boolean;
};
export type DraftScope = { owner: string; key: string; recoverFrom: string | null; persistent: boolean };

// Tab/session storage avoids a browser-wide answer cache. The owner is an exact account
// identity or a random guest session. Logout rotates the guest identity before any read.
export function openDraftScope(storage: Storage, uid: string | null): DraftScope {
  const owner = uid ? `account:${uid}` : 'guest';
  try {
    const previous = JSON.parse(storage.getItem(CONTEXT) || 'null');
    const tab = typeof previous?.tab === 'string' ? previous.tab : crypto.randomUUID();
    const guest = previous?.owner === 'guest' && typeof previous.guest === 'string' ? previous.guest : crypto.randomUUID();
    const key = `${LEGACY_DRAFT_KEY}:v2:${uid ? `account:${uid}` : `guest:${guest}`}:${tab}`;
    const transfer = JSON.parse(storage.getItem(HANDOFF) || 'null');
    const recoverFrom = uid && transfer?.expiresAt > Date.now() && (!transfer.owner || transfer.owner === owner)
      && typeof transfer.key === 'string' && transfer.key.startsWith(`${LEGACY_DRAFT_KEY}:v2:guest:`) ? transfer.key : null;
    if (recoverFrom) storage.setItem(HANDOFF, JSON.stringify({ ...transfer, owner }));
    else if (transfer && (transfer.expiresAt <= Date.now() || transfer.owner || transfer.key !== key)) storage.removeItem(HANDOFF);
    storage.setItem(CONTEXT, JSON.stringify({ tab, guest, owner }));
    return { owner, key, recoverFrom, persistent: true };
  } catch { return { owner, key: `${LEGACY_DRAFT_KEY}:memory:${crypto.randomUUID()}`, recoverFrom: null, persistent: false }; }
}

export function readScopedDraft(storage: Storage, key: string): AssessmentDraft | null {
  const value = JSON.parse(storage.getItem(key) || 'null');
  if (!value || value.key !== key || value.expiresAt <= Date.now() || value.updatedAt > Date.now() + 60_000
    || !Number.isFinite(value.updatedAt) || !Number.isFinite(value.expiresAt) || value.expiresAt > value.updatedAt + DRAFT_TTL) return null;
  const draft = value.draft as AssessmentDraft;
  if (draft?.version !== BEHAVIOR_ASSESSMENT_VERSION || !draft.answers || typeof draft.answers !== 'object'
    || !['started', 'adultConfirmed', 'profilingConsent', 'researchConsent'].every(field => typeof draft[field as keyof AssessmentDraft] === 'boolean')
    || !Number.isFinite(draft.activeSeconds) || !Number.isInteger(draft.pageIndex)
    || Object.entries(draft.answers).some(([id, v]) => !BEHAVIOR_QUESTIONS.some(q => q.id === id) || !Number.isInteger(v) || v < 1 || v > 5)) return null;
  return { ...draft, pageIndex: Math.max(0, Math.min(11, Number(draft.pageIndex) || 0)), activeSeconds: Math.max(0, Number(draft.activeSeconds) || 0) };
}
export function saveScopedDraft(storage: Storage, key: string, draft: AssessmentDraft) {
  const updatedAt = Date.now();
  storage.setItem(key, JSON.stringify({ key, updatedAt, expiresAt: updatedAt + DRAFT_TTL, draft }));
}
export function prepareGuestHandoff(storage: Storage, scope: DraftScope) {
  if (scope.owner !== 'guest' || storage.getItem(`${scope.key}:submission`)) throw new Error('Recover an interrupted submission as a guest before changing accounts.');
  storage.setItem(HANDOFF, JSON.stringify({ key: scope.key, expiresAt: Date.now() + 10 * 60_000 }));
}
export function finishGuestHandoff(storage: Storage) { storage.removeItem(HANDOFF); }
